import type { NoteFx, NoteFxKind } from "./types";

/** Letter class for tracker FX tokens (keep in sync with FX_KINDS). `?` = chance. */
export const NOTE_FX_LETTER_CLASS = "VUDPCAFREGB?";

/**
 * Sentinel `C--` value: hold until next note / note-off / envelope end
 * (no host-scheduled gate noteOff).
 */
export const CUT_OPEN_HOLD = -1;

export function isCutOpenHold(fx: NoteFx | undefined): boolean {
  return fx?.kind === "cut" && fx.value === CUT_OPEN_HOLD;
}

const FX_KINDS: Record<string, NoteFxKind> = {
  V: "vibrato",
  U: "slideUp",
  D: "slideDown",
  P: "portamento",
  C: "cut",
  A: "arpeggio",
  F: "filter",
  R: "retrigger",
  E: "noteDelay",
  G: "gate",
  B: "pan",
  "?": "chance",
};

const FX_LETTERS: Record<NoteFxKind, string> = {
  vibrato: "V",
  slideUp: "U",
  slideDown: "D",
  portamento: "P",
  cut: "C",
  arpeggio: "A",
  filter: "F",
  retrigger: "R",
  noteDelay: "E",
  gate: "G",
  pan: "B",
  chance: "?",
};

const FX_TOKEN_RE = new RegExp(`^([${NOTE_FX_LETTER_CLASS.replace("?", "\\?")}])([0-9A-Fa-f]{1,2})$`, "i");
const FX_START_RE = new RegExp(`^[${NOTE_FX_LETTER_CLASS.replace("?", "\\?")}]`);
const CHANCE_TOKEN_RE = /^\?([0-9]{1,2})$/;
const CUT_HOLD_TOKEN_RE = /^C--$/i;

/** Parse `D2`, `V40`, `C8`, `C--`, `R04`, `E80`, `?50`, `..` */
export function parseFxToken(token: string): NoteFx | undefined {
  const raw = token.trim();
  if (!raw || raw === ".." || raw === "." || raw === "....") {
    return undefined;
  }
  if (CUT_HOLD_TOKEN_RE.test(raw)) {
    return { kind: "cut", value: CUT_OPEN_HOLD };
  }
  // Chance uses decimal 00–99 (not hex), so handle before the hex letter parser.
  const chance = CHANCE_TOKEN_RE.exec(raw);
  if (chance) {
    return {
      kind: "chance",
      value: Math.min(99, Math.max(0, Number.parseInt(chance[1]!, 10))),
    };
  }
  if (raw.startsWith("?")) {
    return undefined;
  }
  const match = FX_TOKEN_RE.exec(raw);
  if (!match) {
    return undefined;
  }
  const kind = FX_KINDS[match[1]!.toUpperCase()];
  if (!kind || kind === "chance") {
    return undefined;
  }
  return { kind, value: Number.parseInt(match[2]!, 16) };
}

/**
 * Parse glued FX (`C7FA0`, `C08V59FC8`, `C04?50`, `C--V40`) into ordered effects.
 * Dots are fillers (leading, trailing, or between effects: `C--...U08`).
 * Tries 1-digit then 2-digit values so `C7FA0` → cut(7)+filter(A0), while
 * `C08V59` → cut(8)+vibrato(59).
 */
export function parseFxList(token: string): NoteFx[] {
  // Upper-case letter FX codes only; keep `?` chance digits decimal.
  const raw = token.trim().replace(/[a-z]/g, ch => ch.toUpperCase());
  if (!raw || /^\.+$/.test(raw)) {
    return [];
  }

  function tryParse(rest: string): NoteFx[] | null {
    // Skip pad dots between / around effects (`C--...U08...`).
    rest = rest.replace(/^\.+/, "");
    if (rest.length === 0) {
      return [];
    }
    if (!FX_START_RE.test(rest)) {
      return null;
    }
    // Open-hold cut before hex width tries (`C--` is not hex digits).
    if (rest.startsWith("C--")) {
      const next = tryParse(rest.slice(3));
      if (next) {
        return [{ kind: "cut", value: CUT_OPEN_HOLD }, ...next];
      }
      return null;
    }
    for (const hexLen of [1, 2] as const) {
      if (rest.length < 1 + hexLen) {
        continue;
      }
      const piece = rest.slice(0, 1 + hexLen);
      const parsed = parseFxToken(piece);
      if (!parsed) {
        continue;
      }
      const next = tryParse(rest.slice(1 + hexLen));
      if (next) {
        return [parsed, ...next];
      }
    }
    return null;
  }

  return tryParse(raw) ?? [];
}

export function formatFx(fx: NoteFx | undefined): string {
  if (!fx) {
    return "..";
  }
  if (fx.kind === "chance") {
    // Decimal percent 00–99 (not hex).
    return `?${Math.min(99, Math.max(0, fx.value | 0)).toString().padStart(2, "0")}`;
  }
  if (isCutOpenHold(fx)) {
    return "C--";
  }
  const letter = FX_LETTERS[fx.kind];
  // Always 2 hex digits so glued lists stay stable in the tracker column.
  return `${letter}${fx.value.toString(16).toUpperCase().padStart(2, "0")}`;
}

export function formatFxList(fx: NoteFx[] | undefined): string {
  if (!fx || fx.length === 0) {
    return "..";
  }
  return fx.map(item => formatFx(item)).join("");
}
